import { ref } from 'vue';
import { useRouter } from 'vue-router';

const STORAGE_KEY = 'musicmint_similar_music_data';
const MAX_PROMPT_LENGTH = 300;
const MAX_TAGS_LENGTH = 200;
const MAX_LYRICS_LENGTH = 3000;

export interface SimilarMusicData {
  prompt: string;
  tags: string;
  instrumental: boolean;
  lyrics: string;
  title: string;
  mode: string;
  source?: string;
  sourceSongId?: string;
  sourcePage?: string;
  sourceSlug?: string;
  sourceKeyword?: string;
  sourceCtaLocation?: string;
}

// 模块级 ref，SPA 跳转时保持
const similarMusicData = ref<SimilarMusicData | null>(null);

const writeToStorage = (data: SimilarMusicData) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
};

const readFromStorage = (): SimilarMusicData | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SimilarMusicData;
  } catch {
    return null;
  }
};

const removeFromStorage = () => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {}
};

const normalizeText = (value: any, maxLength: number) => {
  if (typeof value === 'undefined' || value === null) return '';
  return String(value).trim().slice(0, maxLength);
};

const normalizeSimilarMusicData = (musicData: Partial<SimilarMusicData> & Record<string, any>): SimilarMusicData => {
  const rawPrompt = typeof musicData.prompt === 'string' ? musicData.prompt.trim() : '';
  const rawLyrics = typeof musicData.lyrics === 'string' ? musicData.lyrics.trim() : '';
  const rawTitle = typeof musicData.title === 'string' ? musicData.title.trim() : '';
  const rawTags = typeof musicData.tags === 'string' ? musicData.tags.trim() : '';

  const shouldUseAdvancedMode = musicData.mode === 'advanced'
    || rawLyrics.length > 0
    || rawPrompt.length > MAX_PROMPT_LENGTH;

  const prompt = shouldUseAdvancedMode
    ? normalizeText(rawTitle || rawTags || rawPrompt, MAX_PROMPT_LENGTH)
    : normalizeText(rawPrompt, MAX_PROMPT_LENGTH);

  const lyrics = shouldUseAdvancedMode
    ? normalizeText(rawLyrics || rawPrompt, MAX_LYRICS_LENGTH)
    : normalizeText(rawLyrics, MAX_LYRICS_LENGTH);

  return {
    prompt,
    tags: normalizeText(rawTags, MAX_TAGS_LENGTH),
    instrumental: Boolean(musicData.instrumental),
    lyrics,
    title: normalizeText(rawTitle, 120),
    mode: shouldUseAdvancedMode ? 'advanced' : 'normal',
    source: musicData.source || undefined,
    sourceSongId: musicData.sourceSongId || undefined,
    sourcePage: musicData.sourcePage || undefined,
    sourceSlug: musicData.sourceSlug || undefined,
    sourceKeyword: musicData.sourceKeyword || undefined,
    sourceCtaLocation: musicData.sourceCtaLocation || undefined,
  };
};

export const useMusicCreate = () => {
  const router = useRouter();

  const saveMusicData = (musicData: Partial<SimilarMusicData> & Record<string, any>, currentRoute?: string, createQuery?: Record<string, string>) => {
    const processedData = normalizeSimilarMusicData(musicData);

    similarMusicData.value = processedData;
    writeToStorage(processedData);

    if (currentRoute !== '/create') {
      setTimeout(() => {
        router.push({ path: '/create', ...(createQuery ? { query: createQuery } : {}) });
      }, 100);
    }
  };

  const getSimilarMusicData = (): SimilarMusicData | null => {
    if (similarMusicData.value) return similarMusicData.value;
    const stored = readFromStorage();
    if (stored) {
      similarMusicData.value = stored;
    }
    return stored;
  };

  const clearSimilarMusicData = () => {
    similarMusicData.value = null;
    removeFromStorage();
  };

  return {
    saveMusicData,
    getSimilarMusicData,
    clearSimilarMusicData
  };
};
